/**
 * "Use as aircraft photo…" on a project photo (MB's decision 598, Q523 B; docs/specs/cloud/aircraft-photo.md §4), for
 * tenant admins and managers: choose the aircraft (the one the caption names first), then its display size becomes
 * that aircraft's own photo, shown by the daily and weekly reports in Teslyn Field. The database decides
 * (photoApi.ts).
 */
import { useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Aircraft } from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { SelectField } from '../components/Fields.tsx';
import { loadAircraft } from './api.ts';
import { setAircraftPhotoFromProjectPhoto } from './photoApi.ts';

export interface UseAsAircraftPhotoProps {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  /** The photo's title (caption or file name), to name it and to pick the aircraft it names. */
  readonly title: string;
  /** The display size's object in `project-photos`. */
  readonly display: string;
  readonly newId?: (() => string) | undefined;
}

const errText = (e: unknown): string => (e instanceof Error ? e.message : String(e));

/** The aircraft a caption names ("VH-JOZ at Mt Clere", "JOZ refuel"), else the first. */
export function aircraftNamedIn(list: readonly Aircraft[], title: string): string {
  const text = title.toUpperCase();
  const hit = list.find((a) => {
    const reg = a.registration.toUpperCase();
    const tail = reg.includes('-') ? reg.slice(reg.indexOf('-') + 1) : reg;
    return text.includes(reg) || new RegExp(`(^|[^A-Z0-9])${tail}([^A-Z0-9]|$)`).test(text);
  });
  return (hit ?? list[0])?.id ?? '';
}

export function UseAsAircraftPhoto({
  sb,
  tenantId,
  title,
  display,
  newId,
}: UseAsAircraftPhotoProps): ReactElement {
  const [open, setOpen] = useState(false);
  const [aircraft, setAircraft] = useState<Aircraft[] | null>(null);
  const [chosen, setChosen] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  async function start(): Promise<void> {
    setOpen(true);
    setProblem(null);
    setDone(null);
    try {
      const list = await loadAircraft(sb, tenantId);
      setAircraft(list);
      setChosen(aircraftNamedIn(list, title));
    } catch (e) {
      setProblem(errText(e));
      setAircraft([]);
    }
  }

  async function use(): Promise<void> {
    const target = aircraft?.find((a) => a.id === chosen);
    if (!target) return;
    setBusy(true);
    setProblem(null);
    try {
      const saved = await setAircraftPhotoFromProjectPhoto(sb, target, { display }, newId);
      setDone(`${saved.registration}'s photo is now ${title}.`);
      setOpen(false);
    } catch (e) {
      setProblem(errText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack">
      {!open ? (
        <div className="row row--wrap">
          <Button
            variant="quiet"
            aria-label={`Use ${title} as an aircraft photo`}
            onClick={() => void start()}
          >
            Use as aircraft photo…
          </Button>
        </div>
      ) : (
        <div className="stack" role="group" aria-label={`Use ${title} as an aircraft photo`}>
          {aircraft === null ? (
            <p className="caption muted">Loading the aircraft…</p>
          ) : aircraft.length === 0 ? (
            <p className="body">No aircraft in the register yet.</p>
          ) : (
            <>
              <SelectField<string>
                label="Aircraft"
                value={chosen}
                onChange={setChosen}
                options={aircraft.map((a) => [a.id, a.registration] as const)}
              />
              <p className="caption muted">
                Replaces the aircraft&apos;s photo on the daily and weekly reports, on each laptop after its
                next sync.
              </p>
            </>
          )}
          <div className="row row--wrap">
            {aircraft && aircraft.length > 0 && (
              <Button busy={busy} onClick={() => void use()}>
                {busy ? 'Setting…' : 'Use photo'}
              </Button>
            )}
            <Button variant="quiet" onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      {done && (
        <p className="caption" role="status">
          {done}
        </p>
      )}
      {problem && (
        <p className="notice notice--fail" role="alert">
          {problem}
        </p>
      )}
    </div>
  );
}
